'use client';

import { useCallback, useSyncExternalStore } from 'react';

const listeners = new Set<() => void>();

const subscribe = (callback: () => void) => {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
};

const emit = () => {
  listeners.forEach((listener) => listener());
};

const getSnapshot = (storageKey: string) => () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.localStorage.getItem(storageKey) === 'true';
};

const getServerSnapshot = () => false;

/**
 * SSR-safe helper for a dismissible bar whose dismissed state is persisted in
 * `localStorage`.
 *
 * `isDismissed` is `false` during server rendering and the first client render
 * (so hydration matches), then reflects the persisted value. `dismiss()`
 * persists the dismissal and notifies all subscribers. Implemented with
 * `useSyncExternalStore` so it never calls `setState` inside an effect.
 */
export const useDismissibleBar = (storageKey: string) => {
  const isDismissed = useSyncExternalStore(
    subscribe,
    getSnapshot(storageKey),
    getServerSnapshot,
  );

  const dismiss = useCallback(() => {
    window.localStorage.setItem(storageKey, 'true');
    emit();
  }, [storageKey]);

  return { isDismissed, dismiss };
};
